import { Config } from '@/utils/config';
import { parseInsufficientCreditsResponse, showInsufficientCreditsModal } from '@/lib/credit-guard';
import { getActiveOrgId, whenOrgReady } from '@/lib/active-org';
import { lockIfDocumentSigned } from '@/lib/signed-document-lock';

// Requests carry the acting org as a header. Bootstrap fetches (the ones that learn the org
// from the document) set skipOrgGate so they can run before the org is known; every other
// org-scoped call waits for whenOrgReady() rather than falling back to the session's org.
type OrgAwareRequestInit = RequestInit & { skipOrgGate?: boolean };

function withActiveOrg(headers: Record<string, string>): Record<string, string> {
  const orgId = getActiveOrgId();
  if (orgId == null || 'X-Organization-Id' in headers) {
    return headers;
  }
  return { ...headers, 'X-Organization-Id': String(orgId) };
}

export async function authenticatedFetch(
  endpoint: string,
  options: OrgAwareRequestInit = {},
  overrideHeaders?: Record<string, string>,
): Promise<Response> {
  const { skipOrgGate, ...requestInit } = options;
  if (!skipOrgGate) await whenOrgReady();

  const url = `${Config.NEST_URL}${endpoint}`;
  const headers = withActiveOrg(
    overrideHeaders
      ? overrideHeaders
      : {
          ...(requestInit.body ? { 'Content-Type': 'application/json' } : {}),
          ...((requestInit.headers as Record<string, string>) || {}),
        },
  );

  const response = await fetch(url, { ...requestInit, headers, credentials: 'include' });
  void lockIfDocumentSigned(response);
  return response;
}

export async function authenticatedFormDataFetch(
  endpoint: string,
  options: OrgAwareRequestInit = {},
): Promise<Response> {
  const { skipOrgGate, ...requestInit } = options;
  if (!skipOrgGate) await whenOrgReady();

  const url = `${Config.NEST_URL}${endpoint}`;
  const headers = withActiveOrg({
    ...((requestInit.headers as Record<string, string>) || {}),
  });

  return fetch(url, { ...requestInit, headers, credentials: 'include' });
}

export async function authenticatedNextFetch(
  endpoint: string,
  options: OrgAwareRequestInit = {},
  { onInsufficientCredits = 'modal' }: { onInsufficientCredits?: 'modal' | 'ignore' } = {},
): Promise<Response> {
  const { skipOrgGate, ...requestInit } = options;
  if (!skipOrgGate) await whenOrgReady();

  const url = `${Config.NEXT_URL}${endpoint}`;
  const headers = withActiveOrg({
    ...(requestInit.body ? { 'Content-Type': 'application/json' } : {}),
    ...((requestInit.headers as Record<string, string>) || {}),
  });

  const response = await fetch(url, { ...requestInit, headers, credentials: 'include' });

  if (response.status === 402 && onInsufficientCredits === 'modal') {
    const details = await parseInsufficientCreditsResponse(response);
    if (details) {
      showInsufficientCreditsModal(details);
    }
  }

  return response;
}
